import React, { useEffect, useState } from "react";
import { UserCog, Loader2, KeyRound, Trash2, Plus } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "never");
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Something went wrong";
};

export const StaffSection = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ username: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = () => api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => toast.error("Could not load staff")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/staff", { ...form, role: "packer" });
      toast.success(`${form.username} can now sign in at the staff portal`);
      setForm({ username: "", password: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const resetPassword = async (username) => {
    setBusy(true);
    try {
      await api.post(`/admin/staff/${encodeURIComponent(username)}/password`, { password: newPw });
      toast.success(`Password updated for ${username}`);
      setResetting(null); setNewPw("");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async (username) => {
    if (!window.confirm(`Remove ${username}'s login? They'll be signed out immediately.`)) return;
    try {
      await api.delete(`/admin/staff/${encodeURIComponent(username)}`);
      toast.success(`${username} removed`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="rounded-sm border border-[#1E2E44] bg-[#0F172A] overflow-hidden mb-6" data-testid="staff-section">
      <div className="px-5 py-4 border-b border-[#1E2E44] flex items-center gap-2.5">
        <UserCog size={18} className="text-[#A6FF00]" />
        <h2 className="text-[#F7FAFC] pg-heading text-lg" style={{ fontFamily: "'Rajdhani'" }}>Staff logins</h2>
      </div>
      <div className="px-5 py-5 space-y-5">
        <p className="text-[12px] text-[#9CA8B8] leading-relaxed">
          Packers sign in at the same staff portal as you and see <strong className="text-[#F7FAFC]">only the Dispatch page</strong>: names, addresses and items,
          the Royal Mail CSV, and marking orders shipped. No totals, customer emails, products, reports or settings.
        </p>

        {loading ? (
          <div className="text-[#9CA8B8] text-sm">Loading…</div>
        ) : staff.length === 0 ? (
          <div className="text-[#9CA8B8] text-sm" data-testid="staff-empty">No staff logins yet.</div>
        ) : (
          <ul className="divide-y divide-[#1E2E44] border border-[#1E2E44] rounded-sm" data-testid="staff-list">
            {staff.map((s) => (
              <li key={s.username} className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`staff-row-${s.username}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[#F7FAFC] text-sm pg-heading" style={{ fontFamily: "'Rajdhani'" }}>{s.username} <span className="text-[#5EEAD4] text-xs pg-mono ml-1">{s.role}</span></div>
                  <div className="text-[11px] text-[#9CA8B8]">Last signed in: {fmt(s.last_login_at)}</div>
                </div>
                {resetting === s.username ? (
                  <div className="flex items-center gap-2">
                    <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password (8+ chars)" className="pg-input text-xs w-48" data-testid={`staff-newpw-${s.username}`} />
                    <button onClick={() => resetPassword(s.username)} disabled={busy || newPw.length < 8} className="pg-btn-primary text-xs" data-testid={`staff-savepw-${s.username}`}>Save</button>
                    <button onClick={() => { setResetting(null); setNewPw(""); }} className="pg-btn-ghost text-xs">Cancel</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <button onClick={() => setResetting(s.username)} className="pg-btn-ghost text-xs" data-testid={`staff-reset-${s.username}`}><KeyRound size={12} /> Reset password</button>
                    <button onClick={() => remove(s.username)} className="pg-btn-ghost text-xs text-[#FF6B6B]" data-testid={`staff-remove-${s.username}`}><Trash2 size={12} /> Remove</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid="staff-create-form">
          <label className="block">
            <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8] mb-1.5">Username</div>
            <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.trim() })} placeholder="e.g. George" className="pg-input w-full" data-testid="staff-username-input" />
          </label>
          <label className="block">
            <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8] mb-1.5">Password (8+ characters)</div>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="pg-input w-full" data-testid="staff-password-input" />
          </label>
          <button onClick={create} disabled={busy || form.username.length < 2 || form.password.length < 8} className="pg-btn-primary text-xs" data-testid="staff-create-button">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add packer
          </button>
        </div>
      </div>
    </section>
  );
};
